import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthService } from './services/authService';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import RoleRouter from './components/RoleRouter';
import './index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkSession = async () => {
      try {
        const current = await AuthService.getCurrentUser();
        if (current) {
          setUser(current);
          await AuthService.restoreSupabaseSession().catch(() => {});
        }
      } catch (error) {
        console.error('Error checking session:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkSession();
    AuthService.initSessionGuard();

    const restoreIfNeeded = () => {
      AuthService.getCurrentUser()
        .then((current) => {
          if (current) setUser(current);
        })
        .catch(() => {});
      AuthService.restoreSupabaseSession().catch(() => {});
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') restoreIfNeeded();
    };

    window.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pageshow', restoreIfNeeded);
    window.addEventListener('focus', restoreIfNeeded);
    return () => {
      window.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pageshow', restoreIfNeeded);
      window.removeEventListener('focus', restoreIfNeeded);
    };
  }, []);

  const handleLogin = async (email, password) => {
    const result = await AuthService.login(email, password);
    if (result.success) {
      setUser(result.user);
      return { success: true };
    }
    return { success: false, error: result.error || 'Login failed' };
  };

  const handleLogout = async () => {
    await AuthService.logout();
    setUser(null);
  };

  // Get default route based on role
  const getDefaultRoute = () => {
    if (!user) return '/login';
    let role = user.primaryRole || user.currentRole || 'customer';
    
    // Normalize role names for routing
    if (role === 'serviceadvisor') role = 'service_advisor';
    if (role === 'serviceexecutive') role = 'service_executive';
    
    // Admin routes are at root level
    if (role === 'admin') return '/dashboard';
    // All other roles use role prefix
    return `/${role}/dashboard`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-primary-500 to-purple-600">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-white border-t-transparent rounded-full spinner mx-auto mb-4"></div>
          <p className="text-white text-lg font-medium">Loading RoadServe Admin...</p>
        </div>
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <Routes>
          <Route
            path="/login"
            element={
              user ? (
                <Navigate to={getDefaultRoute()} replace />
              ) : (
                <LoginPage onLogin={handleLogin} />
              )
            }
          />
          <Route
            path="/forgot-password"
            element={
              user ? (
                <Navigate to={getDefaultRoute()} replace />
              ) : (
                <ForgotPasswordPage />
              )
            }
          />
          <Route
            path="/*"
            element={
              user ? (
                <RoleRouter user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
        </Routes>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
